import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { ApiError } from '../../api/client';
import { blogApi } from '../../api/blog';
import BlogNav from './BlogNav';

function kilobytes(bytes: number): string {
  return bytes < 1024 * 1024 ? `${Math.max(1, Math.round(bytes / 1024))} KB` : `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export default function BlogMediaPage() {
  const queryClient = useQueryClient();
  const input = useRef<HTMLInputElement>(null);

  const [page, setPage] = useState(1);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState<string | null>(null);

  const { data, isPending } = useQuery({ queryKey: ['blog-media', page], queryFn: () => blogApi.media(page) });

  function fail(e: unknown) {
    setError(e instanceof ApiError ? e.message : 'Something went wrong.');
  }

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['blog-media'] });

  const upload = useMutation({
    mutationFn: async (files: File[]) => {
      for (const file of files) {
        await blogApi.upload(file);
      }
    },
    onSuccess: async () => {
      setError(null);
      setPage(1);
      await refresh();
    },
    onError: fail,
  });

  const remove = useMutation({
    mutationFn: (id: string) => blogApi.removeMedia(id),
    onSuccess: async () => {
      setError(null);
      await refresh();
    },
    onError: fail,
  });

  async function copy(url: string) {
    // A relative URL means the local development store; make it something that can be pasted.
    const absolute = url.startsWith('http') ? url : `${window.location.origin}${url}`;

    try {
      await navigator.clipboard.writeText(absolute);
      setCopied(url);
      window.setTimeout(() => setCopied((current) => (current === url ? null : current)), 1500);
    } catch {
      setError('The address could not be copied. Select it from the image instead.');
    }
  }

  return (
    <>
      <BlogNav />

      <section className="card">
        <div className="topbar topbar--flush">
          <h2>Media</h2>
          <button type="button" className="btn btn--primary" disabled={upload.isPending} onClick={() => input.current?.click()}>
            {upload.isPending ? 'Uploading…' : 'Upload images'}
          </button>
        </div>

        <p className="muted">
          Every upload is decoded, fitted to 1600 px and re-encoded as WebP before it is stored — what is kept is the
          pixels, not the file that was sent. JPEG, PNG and WebP are accepted; SVG is not, because an SVG can carry
          script. Uploading the same picture twice gives back the first copy.
        </p>

        <input
          ref={input}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          multiple
          hidden
          onChange={(e) => {
            const files = Array.from(e.target.files ?? []);
            e.target.value = '';
            if (files.length > 0) {
              upload.mutate(files);
            }
          }}
        />

        {error && <p className="status-error">{error}</p>}
        {isPending && <p className="muted">Loading…</p>}
        {data && data.items.length === 0 && <p className="muted">Nothing uploaded yet.</p>}

        {data && data.items.length > 0 && (
          <>
            <div className="media-grid">
              {data.items.map((asset) => (
                <figure key={asset.id} className="media-grid__item">
                  <img src={asset.url} alt={asset.alt ?? ''} loading="lazy" />
                  <figcaption>
                    <span className="muted">
                      {asset.width}×{asset.height} · {kilobytes(asset.bytes)}
                    </span>
                    <div className="toolbar">
                      <button type="button" className="btn btn--small" onClick={() => void copy(asset.url)}>
                        {copied === asset.url ? 'Copied' : 'Copy address'}
                      </button>
                      <button
                        type="button"
                        className="btn btn--small"
                        disabled={remove.isPending}
                        onClick={() => {
                          if (window.confirm('Delete this image? It is refused while a post still uses it.')) {
                            remove.mutate(asset.id);
                          }
                        }}
                      >
                        Delete
                      </button>
                    </div>
                  </figcaption>
                </figure>
              ))}
            </div>

            <div className="toolbar">
              <button type="button" className="btn btn--small" disabled={!data.hasPrevious} onClick={() => setPage(page - 1)}>
                ←
              </button>
              <span className="muted">
                {data.page} / {Math.max(1, data.totalPages)} · {data.totalCount} images
              </span>
              <button type="button" className="btn btn--small" disabled={!data.hasNext} onClick={() => setPage(page + 1)}>
                →
              </button>
            </div>
          </>
        )}
      </section>
    </>
  );
}
